import { useEffect, useRef, type ReactNode } from 'react';

export interface DialogoProps {
  readonly abierto: boolean;
  readonly titulo: string;
  readonly children: ReactNode;
  /** Los botones. Van a la derecha, el de confirmar el ultimo. */
  readonly acciones: ReactNode;
  /** Escape, clic fuera, o la X. */
  readonly onCerrar: () => void;
  /** Avisa de que lo que se va a hacer no tiene vuelta atras. */
  readonly grave?: boolean;
}

/**
 * Una ventana propia, encima de la pagina.
 *
 * Se apoya en el elemento `<dialog>` del navegador y no en un div flotante.
 * Eso no es purismo: `showModal()` regala cuatro cosas que escribir a mano
 * sale mal casi siempre —el foco encerrado dentro mientras esta abierta, el
 * resto de la pagina marcado como inerte para los lectores de pantalla, la
 * tecla Escape, y pintarse en la capa superior por encima de cualquier
 * `z-index`—. Sin librerias.
 *
 * Sustituye a `confirm()` del navegador, que ponia "app.apuntaqr.com dice"
 * encima del texto, no se puede vestir, y congela la pestana entera mientras
 * esta abierta.
 */
export function Dialogo({
  abierto,
  titulo,
  children,
  acciones,
  onCerrar,
  grave = false,
}: DialogoProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    // `open` a mano NO vale: abre la ventana sin modalidad, sin capa superior
    // y sin encerrar el foco. Tiene que ser showModal().
    if (abierto && !d.open) d.showModal();
    if (!abierto && d.open) d.close();
  }, [abierto]);

  if (!abierto) return null;

  return (
    <dialog
      ref={ref}
      className="qr-dialogo"
      aria-labelledby="qr-dialogo-titulo"
      // El navegador cierra con Escape por su cuenta; esto mantiene enterado
      // a React, que si no se queda creyendo que sigue abierta.
      onCancel={(e) => {
        e.preventDefault();
        onCerrar();
      }}
      // Clic en el fondo oscuro. El fondo es el propio <dialog>: lo que hay
      // dentro es el recuadro, asi que un clic cuyo destino sea el dialogo en
      // si es un clic fuera del contenido.
      onClick={(e) => {
        if (e.target === ref.current) onCerrar();
      }}
    >
      <div className="qr-dialogo__caja">
        <h2 className="qr-dialogo__titulo" id="qr-dialogo-titulo">
          {titulo}
        </h2>
        <div className={`qr-dialogo__cuerpo${grave ? ' qr-dialogo__cuerpo--grave' : ''}`}>
          {children}
        </div>
        <div className="qr-dialogo__acciones">{acciones}</div>
      </div>
    </dialog>
  );
}
